import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader } from '../../components/common';
import { FiClock, FiCheck, FiX, FiCalendar, FiPlus, FiTrash2 } from 'react-icons/fi';
import axiosClient from '../../api/axios';

interface WeekdaySlot {
  day: string;
  dayIndex: number;
  enabled: boolean;
  startTime: string;
  endTime: string;
}

interface BlockedDate {
  date: string;
  reason?: string;
}

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return [`${hour}:00`, `${hour}:30`];
}).flat();

const AvailabilityManagement: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);

  const [weeklySchedule, setWeeklySchedule] = useState<WeekdaySlot[]>(
    WEEKDAYS.map((day, index) => ({
      day,
      dayIndex: index,
      enabled: index < 5, // Mon-Fri enabled by default
      startTime: '09:00',
      endTime: '18:00',
    }))
  );

  const [blockedDates, setBlockedDates] = useState<BlockedDate[]>([]);
  const [newBlockedDate, setNewBlockedDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fetch existing availability
  useEffect(() => {
    const fetchAvailability = async () => {
      setLoading(true);
      try {
        const response = await axiosClient.get('bookings/expert_weekday_availability/');
        if (response.data?.schedule) {
          setWeeklySchedule(response.data.schedule);
        }
        if (response.data?.blocked_dates) {
          setBlockedDates(response.data.blocked_dates);
        }
      } catch (err) {
        console.log('No existing availability found');
      } finally {
        setLoading(false);
      }
    };
    fetchAvailability();
  }, []);

  const toggleDay = (dayIndex: number) => {
    setWeeklySchedule((prev) =>
      prev.map((slot) =>
        slot.dayIndex === dayIndex ? { ...slot, enabled: !slot.enabled } : slot
      )
    );
  };

  const updateTime = (dayIndex: number, field: 'startTime' | 'endTime', value: string) => {
    setWeeklySchedule((prev) =>
      prev.map((slot) =>
        slot.dayIndex === dayIndex ? { ...slot, [field]: value } : slot
      )
    );
  };

  const addBlockedDate = () => {
    if (!newBlockedDate) return;
    const today = new Date().toISOString().split('T')[0];
    if (newBlockedDate < today) {
      setMessage({ type: 'error', text: 'Cannot block past dates' });
      return;
    }
    if (blockedDates.some((d) => d.date === newBlockedDate)) {
      setMessage({ type: 'error', text: 'Date already blocked' });
      return;
    }
    setBlockedDates((prev) => [...prev, { date: newBlockedDate }]);
    setNewBlockedDate('');
  };

  const removeBlockedDate = (date: string) => {
    setBlockedDates((prev) => prev.filter((d) => d.date !== date));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axiosClient.post('bookings/expert_weekday_availability/', {
        schedule: weeklySchedule,
        blocked_dates: blockedDates,
      });
      setMessage({ type: 'success', text: 'Availability saved successfully!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save availability' });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: colors.backgroundColor }}
      >
        <CText type="M14" color={colors.grayScale500}>
          Loading availability...
        </CText>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <div className="p-4 lg:p-8">
        <div className="lg:max-w-2xl lg:mx-auto">
          <CHeader title="Availability" />

          {/* Message */}
          {message && (
            <div
              className="mb-4 p-3 rounded-xl flex items-center gap-2"
              style={{
                backgroundColor: message.type === 'success' ? colors.green + '20' : colors.alertColor + '20',
              }}
            >
              {React.createElement(
                message.type === 'success' ? FiCheck : FiX as any,
                { size: 18, color: message.type === 'success' ? colors.green : colors.alertColor }
              )}
              <CText
                type="r14"
                color={message.type === 'success' ? colors.green : colors.alertColor}
              >
                {message.text}
              </CText>
            </div>
          )}

          {/* Weekly Schedule */}
          <div
            className="rounded-2xl p-5 mb-6"
            style={{
              backgroundColor: colors.cardBackground || colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              {React.createElement(FiClock as any, { size: 20, color: colors.primary })}
              <CText type="B16" color={colors.textColor}>
                Weekly Schedule
              </CText>
            </div>

            <div className="space-y-3">
              {weeklySchedule.map((slot) => (
                <div
                  key={slot.day}
                  className="flex items-center gap-3 p-3 rounded-xl"
                  style={{
                    backgroundColor: slot.enabled ? colors.primary + '10' : colors.backgroundColor,
                  }}
                >
                  {/* Toggle */}
                  <button
                    onClick={() => toggleDay(slot.dayIndex)}
                    className="w-10 h-6 rounded-full relative transition-colors"
                    style={{
                      backgroundColor: slot.enabled ? colors.primary : colors.grayScale400,
                    }}
                  >
                    <div
                      className="absolute top-1 w-4 h-4 rounded-full bg-white transition-all"
                      style={{ left: slot.enabled ? '22px' : '4px' }}
                    />
                  </button>

                  {/* Day name */}
                  <div className="w-24">
                    <CText
                      type="M14"
                      color={slot.enabled ? colors.textColor : colors.grayScale500}
                    >
                      {slot.day.slice(0, 3)}
                    </CText>
                  </div>

                  {/* Time selectors */}
                  {slot.enabled && (
                    <div className="flex items-center gap-2 flex-1">
                      <select
                        value={slot.startTime}
                        onChange={(e) => updateTime(slot.dayIndex, 'startTime', e.target.value)}
                        className="px-3 py-2 rounded-lg text-sm outline-none"
                        style={{
                          backgroundColor: colors.backgroundColor,
                          color: colors.textColor,
                          border: `1px solid ${colors.borderColor}`,
                        }}
                      >
                        {TIME_OPTIONS.map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                      <CText type="r12" color={colors.grayScale500}>
                        to
                      </CText>
                      <select
                        value={slot.endTime}
                        onChange={(e) => updateTime(slot.dayIndex, 'endTime', e.target.value)}
                        className="px-3 py-2 rounded-lg text-sm outline-none"
                        style={{
                          backgroundColor: colors.backgroundColor,
                          color: colors.textColor,
                          border: `1px solid ${colors.borderColor}`,
                        }}
                      >
                        {TIME_OPTIONS.map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Blocked Dates */}
          <div
            className="rounded-2xl p-5 mb-6"
            style={{
              backgroundColor: colors.cardBackground || colors.inputBackground,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <div className="flex items-center gap-2 mb-4">
              {React.createElement(FiCalendar as any, { size: 20, color: colors.alertColor })}
              <CText type="B16" color={colors.textColor}>
                Blocked Dates
              </CText>
            </div>

            <CText type="r12" color={colors.grayScale500} style={{ marginBottom: 12 }}>
              Block specific dates when you're unavailable (holidays, leave, etc.)
            </CText>

            {/* Add blocked date */}
            <div className="flex gap-2 mb-4">
              <input
                type="date"
                value={newBlockedDate}
                onChange={(e) => setNewBlockedDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="flex-1 px-4 py-2 rounded-xl outline-none"
                style={{
                  backgroundColor: colors.backgroundColor,
                  color: colors.textColor,
                  border: `1px solid ${colors.borderColor}`,
                }}
              />
              <button
                onClick={addBlockedDate}
                className="px-4 py-2 rounded-xl flex items-center gap-2"
                style={{ backgroundColor: colors.primary }}
              >
                {React.createElement(FiPlus as any, { size: 18, color: '#fff' })}
                <CText type="M12" color="#fff">
                  Add
                </CText>
              </button>
            </div>

            {/* Blocked dates list */}
            {blockedDates.length > 0 ? (
              <div className="space-y-2">
                {blockedDates.map((blocked) => (
                  <div
                    key={blocked.date}
                    className="flex items-center justify-between p-3 rounded-xl"
                    style={{ backgroundColor: colors.alertColor + '10' }}
                  >
                    <CText type="r14" color={colors.textColor}>
                      {formatDate(blocked.date)}
                    </CText>
                    <button
                      onClick={() => removeBlockedDate(blocked.date)}
                      className="p-2"
                    >
                      {React.createElement(FiTrash2 as any, { size: 16, color: colors.alertColor })}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <CText type="r12" color={colors.grayScale500} style={{ fontStyle: 'italic' }}>
                No dates blocked
              </CText>
            )}
          </div>

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-4 rounded-2xl font-semibold transition-all"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
              color: '#fff',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'Saving...' : 'Save Availability'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityManagement;
